import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import {
  Check,
  Clock,
  DollarSign,
  Upload,
  ListTodo,
  Eye,
  Pencil,
} from "lucide-react";
import { Modal } from "@/components/Usados/modal";
import Input from "@/components/Usados/InputField";
import { apiFetch } from "@/service/api";

export default function TableroActividades({ onEdit }: any) {
  const { id: proyecto_id } = useParams();
  const api = import.meta.env.VITE_API_URL_BACKEND;
  const [tablero, setTablero] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [puntosModal, setPuntosModal] = useState<any>(null);
  const [puntos, setPuntos] = useState<any[]>([]);
  const [nuevoPunto, setNuevoPunto] = useState("");

  const fetchTablero = async () => {
    setLoading(true);
    const res = await apiFetch(`actividades-proyectos/tablero/${proyecto_id}`);
    if (res.ok) setTablero(await res.json());
    setLoading(false);
  };
  useEffect(() => {
    fetchTablero();
  }, [proyecto_id]);

  const fetchPuntos = async (actividad_id: number) => {
    const res = await apiFetch(`puntos-actividad/actividad/${actividad_id}`);
    if (res.ok) setPuntos(await res.json());
  };

  const openPuntos = (act: any) => {
    setPuntosModal(act);
    fetchPuntos(act.id);
  };

  const crearPunto = async () => {
    if (!nuevoPunto.trim()) return;
    await apiFetch(`puntos-actividad/create`, {
      method: "POST",
      body: JSON.stringify({
        actividad_id: puntosModal.id,
        nombre: nuevoPunto,
      }),
    });
    setNuevoPunto("");
    fetchPuntos(puntosModal.id);
    fetchTablero();
  };

  const togglePunto = async (p: any) => {
    await apiFetch(`puntos-actividad/update/${p.id}`, {
      method: "PATCH",
      body: JSON.stringify({ completado: !p.completado }),
    });
    fetchPuntos(puntosModal.id);
    fetchTablero();
  };

  const uploadSoporte = async (actId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    await apiFetch(`actividades-proyectos/upload-soporte/${actId}`, {
      method: "POST",
      body: fd,
    });
    fetchTablero();
  };

  const getSoporteUrl = (path: string) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    if (path.startsWith("/")) return `${api.replace("/api", "")}${path}`;
    return `${api}/files/${path}`;
  };

  if (loading)
    return <div className="p-10 text-center">Cargando tablero...</div>;
  if (!tablero) return <div>No hay datos</div>;

  const columnas = [
    {
      key: "pendiente",
      label: "Pendiente",
      color: "bg-gray-50 border-gray-200",
      icon: <Clock className="size-4" />,
    },
    {
      key: "en_progreso",
      label: "En Progreso",
      color: "bg-amber-50 border-amber-200",
      icon: <Clock className="size-4 text-amber-600" />,
    },
    {
      key: "completada",
      label: "Completado",
      color: "bg-green-50 border-green-200",
      icon: <Check className="size-4 text-green-600" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold flex gap-2 items-center">
          <ListTodo className="size-5 text-brand-500" /> Tablero de Actividades
          / Tareas
        </h3>
        <div className="flex gap-2">
          <Badge color="light">
            Costo Actividades: $
            {Number(tablero.total_costo || 0).toLocaleString()}
          </Badge>
          <Badge color="info">{tablero.total?.length || 0} Actividades</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columnas.map((col) => (
          <div
            key={col.key}
            className={`rounded-2xl border-2 p-4 min-h- ${col.color}`}
          >
            <h4 className="font-semibold mb-4 flex gap-2 items-center justify-between">
              <span className="flex gap-2 items-center">
                {col.icon} {col.label}
              </span>
              <Badge color="light">{tablero[col.key]?.length || 0}</Badge>
            </h4>
            <div className="space-y-3">
              {tablero[col.key]?.map((act: any) => (
                <div
                  key={act.id}
                  className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <p className="font-medium text-sm line-clamp-2">
                      {act.nombre}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit(act)}
                    >
                      <Pencil className="size-3" />
                    </Button>
                  </div>
                  <p className="text- text-gray-500 mt-1 line-clamp-2">
                    {act.descripcion}
                  </p>
                  <div className="mt-3">
                    <div className="flex justify-between text- mb-1">
                      <span>Progreso</span>
                      <span>{act.progreso || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-brand-500 to-green-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${act.progreso || 0}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between mt-3 text-xs">
                    <span className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full">
                      <DollarSign className="size-3" /> $
                      {Number(act.costo || 0).toLocaleString()}
                    </span>
                    <span className="text- text-gray-500">
                      {act.fecha_inicio?.split("T")[0]} →{" "}
                      {act.fecha_fin_estimada?.split("T")[0]}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text- font-bold uppercase text-gray-500">
                      Puntos (
                      {act.puntos?.filter((p: any) => p.completado).length || 0}
                      /{act.puntos?.length || 0})
                    </p>
                    {act.puntos?.slice(0, 3).map((p: any) => (
                      <div key={p.id} className="flex gap-2 text-">
                        <input
                          type="checkbox"
                          checked={p.completado}
                          readOnly
                          className="size-3"
                        />
                        {p.nombre}
                      </div>
                    ))}
                    {act.puntos?.length > 3 && (
                      <p className="text- text-gray-400">
                        +{act.puntos.length - 3} más
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => openPuntos(act)}
                    >
                      <Eye className="size-3" /> Puntos
                    </Button>
                    <label className="flex-1 text-xs border rounded-lg px-2 py-1.5 flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-50">
                      <Upload className="size-3" /> Soporte
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) =>
                          e.target.files &&
                          uploadSoporte(act.id, e.target.files[0])
                        }
                      />
                    </label>
                  </div>
                  {act.soporte_url && (
                    <a
                      href={getSoporteUrl(act.soporte_url)!}
                      target="_blank"
                      className="text- text-blue-600 mt-2 block"
                    >
                      Ver soporte adjunto
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {puntosModal && (
        <Modal
          isOpen={!!puntosModal}
          onClose={() => setPuntosModal(null)}
          className="max-w-lg"
        >
          <div className="p-6">
            <h4 className="font-semibold mb-4">
              Puntos de Actividad: {puntosModal.nombre}
            </h4>
            <div className="flex gap-2 mb-4">
              <Input
                value={nuevoPunto}
                onChange={(e: any) => setNuevoPunto(e.target.value)}
                placeholder="Nuevo punto (ej: Levantamiento topográfico)"
              />
              <Button onClick={crearPunto}>Agregar</Button>
            </div>
            <div className="space-y-2 max-h- overflow-auto">
              {puntos.map((p) => (
                <label
                  key={p.id}
                  className="flex gap-3 p-2 border rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={p.completado}
                    onChange={() => togglePunto(p)}
                  />
                  <span
                    className={`text-sm ${
                      p.completado ? "line-through text-gray-400" : ""
                    }`}
                  >
                    {p.nombre}
                  </span>
                </label>
              ))}
              {puntos.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">
                  Sin puntos. Agrega el primero.
                </p>
              )}
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">
                Progreso: {puntos.filter((p) => p.completado).length}/
                {puntos.length} ={" "}
                {puntos.length
                  ? Math.round(
                      (puntos.filter((p) => p.completado).length /
                        puntos.length) *
                        100,
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
